import { ScimConfigurationResponse } from '@mews-commander/data-access';
import { ArticlePath, createGuideLink, TextKey } from '@mews-commander/enums';
import { NewTabLink, UnsavedChangesDialog } from '@mews-commander/ui';
import { SidePanel } from '@mews/b2b-ui';
import { LocalizedText, useDialog } from '@mews/framework';
import { Button, IconButton, IconName, Stack } from '@optimus-web/core';
import { ScimStatusTag } from '../ScimStatusTag';
import { ConfigureScimPanelContent } from './ConfigureScimPanelContent/ConfigureScimPanelContent';
import { useConfigureScimForm } from './useConfigureScimForm';
import { DisableScimWarningDialog } from './DisableScimWarningDialog';

export interface ConfigureScimPanelProps {
    data?: ScimConfigurationResponse;
    loading: boolean;
    active: boolean;
    onClose: () => void;
}

const formId = 'configure-scim-panel-form';

export function ConfigureScimPanel({ data, loading, active, onClose, ...props }: ConfigureScimPanelProps) {
    const [{ active: showUnsavedChangesDialog }, setUnsavedChangesDialogVisibility] = useDialog();
    const [disableScimWarningDialogProps, toggleDisableScimWarningDialogVisibility] = useDialog();

    const { reset, isSubmitting, hasDirtyFields, submit, renderFormContent, watch, isValid } = useConfigureScimForm({
        id: formId,
        data,
        onSuccess: onClose,
    });
    const willActivateScim = watch('enabled');

    const handleClose = hasDirtyFields ? setUnsavedChangesDialogVisibility : onClose;
    const handleSubmit = (e: React.MouseEvent) => {
        e.preventDefault();
        if (willActivateScim) {
            void submit();
        } else {
            toggleDisableScimWarningDialogVisibility();
        }
    };

    return (
        <>
            <SidePanel
                opened={active}
                onClose={handleClose}
                loading={loading}
                title={
                    <Stack spacing="150" itemAlignment="center">
                        <LocalizedText textKey={TextKey.ScimProvisioning} />
                        <ScimStatusTag provisioningStatus={data?.provisioningStatus} />
                    </Stack>
                }
                content={renderFormContent(<ConfigureScimPanelContent />)}
                width="medium"
                headerActions={
                    <NewTabLink url={createGuideLink(ArticlePath.SetUpScimUserProvisioning)}>
                        <IconButton variant="tertiary" icon={IconName.Help} type="submit" />
                    </NewTabLink>
                }
                actions={
                    <Stack distribution="space-between">
                        <Button size="medium" variant="tertiary" type="button" onClick={handleClose}>
                            <LocalizedText textKey={TextKey.GoBack} />
                        </Button>
                        <Button
                            size="medium"
                            type="submit"
                            variant="primary"
                            form={formId}
                            loading={isSubmitting}
                            onClick={handleSubmit}
                            disabled={!hasDirtyFields || !isValid}
                        >
                            <LocalizedText textKey={TextKey.SaveChanges} />
                        </Button>
                    </Stack>
                }
                {...props}
            />
            <UnsavedChangesDialog
                active={showUnsavedChangesDialog}
                onCancel={setUnsavedChangesDialogVisibility}
                onDiscardChanges={() => {
                    setUnsavedChangesDialogVisibility();
                    onClose();
                    reset();
                }}
            />
            <DisableScimWarningDialog {...disableScimWarningDialogProps} onSubmit={submit} />
        </>
    );
}
